/**
 * TanStack Query hooks for the inventory feature. Keys are namespaced
 * "inventory/…"; cursor lists ride the shared ops-api helpers over the
 * `{ items, next_cursor }` envelope (limit ≤ 200 enforced by the wrapper).
 *
 * Analytics endpoints publish page-level metadata beside their items
 * (`as_of`, `threshold_days`, `bucket_definitions`), so those use one
 * `useInfiniteQuery` that yields BOTH a DataTable-shaped cursor result and
 * the first page's metadata — never a second request for the same page.
 */
import { useMemo } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  useCursorList,
  type CursorQueryResult,
  type ListFilters,
  type SortState,
} from "@/features/shared/ops-api";

import {
  getCountWorksheetEntry,
  getCountWorksheetReview,
  getDeadStockReport,
  getMovement,
  getSlowMoversReport,
  getTransfer,
  getVariantStats,
  getWarehouse,
  listAllLocations,
  listAllWarehouses,
  listBranches,
  listCategories,
  listCountWorksheets,
  listActiveUoms,
  listLocations,
  listMovements,
  listStockLevels,
  listTransfers,
  listWarehouses,
  lookupBatches,
  searchVariants,
  type Batch,
  type BranchOption,
  type BucketDefinition,
  type CategoryOption,
  type CountWorksheetStatus,
  type CountWorksheetSummary,
  type DeadStockItem,
  type DeadStockPage,
  type DeadStockQuery,
  type ReportGroupBy,
  type SlowMoverItem,
  type SlowMoverPage,
  type SlowMoverQuery,
  type StockLevel,
  type StockMovement,
  type StockMovementSourceType,
  type StockMovementType,
  type TransferStatus,
  type TransferSummary,
  type UomOption,
  type VariantOption,
  type VariantStatsQuery,
  type Warehouse,
  type WarehouseLocation,
  type WarehouseLocationStatus,
  type WarehouseLocationType,
  type WarehouseStatus,
} from "./api";

export const inventoryKeys = {
  warehouses: {
    all: ["inventory/warehouses"] as const,
    list: (filters: Record<string, unknown>) => ["inventory/warehouses", "list", filters] as const,
    options: () => ["inventory/warehouses", "options"] as const,
    detail: (id: string) => ["inventory/warehouses", "detail", id] as const,
    locations: (warehouseId: string, filters: Record<string, unknown>) =>
      ["inventory/warehouses", "locations", warehouseId, filters] as const,
    locationOptions: (warehouseId: string) =>
      ["inventory/warehouses", "location-options", warehouseId] as const,
  },
  stock: {
    all: ["inventory/stock"] as const,
    list: (filters: Record<string, unknown>) => ["inventory/stock", "list", filters] as const,
  },
  movements: {
    all: ["inventory/movements"] as const,
    list: (filters: Record<string, unknown>) => ["inventory/movements", "list", filters] as const,
    detail: (id: string) => ["inventory/movements", "detail", id] as const,
  },
  transfers: {
    all: ["inventory/transfers"] as const,
    list: (filters: Record<string, unknown>) => ["inventory/transfers", "list", filters] as const,
    detail: (id: string) => ["inventory/transfers", "detail", id] as const,
  },
  counts: {
    all: ["inventory/counts"] as const,
    list: (filters: Record<string, unknown>) => ["inventory/counts", "list", filters] as const,
    entry: (id: string) => ["inventory/counts", "entry", id] as const,
    review: (id: string) => ["inventory/counts", "review", id] as const,
  },
  reports: {
    all: ["inventory/reports"] as const,
    deadStock: (params: Record<string, unknown>) =>
      ["inventory/reports", "dead-stock", params] as const,
    slowMovers: (params: Record<string, unknown>) =>
      ["inventory/reports", "slow-movers", params] as const,
    variantStats: (variantId: string, params: Record<string, unknown>) =>
      ["inventory/reports", "variant-stats", variantId, params] as const,
  },
  lookups: {
    all: ["inventory/lookups"] as const,
    variants: (q: string) => ["inventory/lookups", "variants", q] as const,
    uoms: () => ["inventory/lookups", "uoms"] as const,
    categories: () => ["inventory/lookups", "categories"] as const,
    branches: () => ["inventory/lookups", "branches"] as const,
    batches: (params: Record<string, unknown>) => ["inventory/lookups", "batches", params] as const,
  },
};

// ── Warehouses & locations ───────────────────────────────────────────────

export interface WarehouseListFilters extends ListFilters {
  q?: string;
  status?: WarehouseStatus;
  branch_id?: string;
}

export function useWarehouseList(
  filters: WarehouseListFilters,
  limit?: number,
): CursorQueryResult<Warehouse> {
  return useCursorList(
    inventoryKeys.warehouses.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listWarehouses(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

/** Active warehouses for filters and form pickers. */
export function useWarehouseOptions(enabled = true) {
  return useQuery<Warehouse[]>({
    queryKey: inventoryKeys.warehouses.options(),
    queryFn: ({ signal }) => listAllWarehouses(signal),
    enabled,
  });
}

/** Warehouse body + ETag snapshot; the snapshot validates the next PATCH. */
export type WarehouseSnapshot = Awaited<ReturnType<typeof getWarehouse>>;

export function useWarehouseDetail(warehouseId: string | null, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.warehouses.detail(warehouseId ?? ""),
    queryFn: (): Promise<WarehouseSnapshot> => getWarehouse(warehouseId as string),
    enabled: warehouseId !== null && enabled,
  });
}

export interface LocationListFilters extends ListFilters {
  location_type?: WarehouseLocationType;
  status?: WarehouseLocationStatus;
}

export function useLocationList(
  warehouseId: string | null,
  filters: LocationListFilters,
  limit?: number,
): CursorQueryResult<WarehouseLocation> {
  return useCursorList(
    inventoryKeys.warehouses.locations(warehouseId ?? "", { ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listLocations(
        warehouseId as string,
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit, enabled: warehouseId !== null },
  );
}

/** Every bin of one warehouse — location pickers in the posting forms. */
export function useLocationOptions(warehouseId: string | null, enabled = true) {
  return useQuery<WarehouseLocation[]>({
    queryKey: inventoryKeys.warehouses.locationOptions(warehouseId ?? ""),
    queryFn: ({ signal }) => listAllLocations(warehouseId as string, signal),
    enabled: warehouseId !== null && warehouseId !== "" && enabled,
  });
}

// ── Stock ────────────────────────────────────────────────────────────────

export interface StockLevelFilters extends ListFilters {
  sku?: string;
  variant_id?: string;
  warehouse_id?: string;
  location_id?: string;
  category_id?: string;
  batch_id?: string;
  nonzero?: boolean;
  available_only?: boolean;
}

export function useStockLevels(
  filters: StockLevelFilters,
  limit?: number,
): CursorQueryResult<StockLevel> {
  return useCursorList(
    inventoryKeys.stock.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listStockLevels(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

// ── Movements ────────────────────────────────────────────────────────────

export interface MovementFilters extends ListFilters {
  variant_id?: string;
  warehouse_id?: string;
  location_id?: string;
  batch_id?: string;
  movement_type?: StockMovementType;
  source_type?: StockMovementSourceType;
  source_id?: string;
  transfer_id?: string;
  occurred_from?: string;
  occurred_to?: string;
}

export function useMovementList(
  filters: MovementFilters,
  limit?: number,
): CursorQueryResult<StockMovement> {
  return useCursorList(
    inventoryKeys.movements.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listMovements(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

export function useMovementDetail(movementId: string | null) {
  return useQuery<StockMovement>({
    queryKey: inventoryKeys.movements.detail(movementId ?? ""),
    queryFn: () => getMovement(movementId as string),
    enabled: movementId !== null && movementId !== "",
  });
}

// ── Transfers ────────────────────────────────────────────────────────────

export interface TransferFilters extends ListFilters {
  status?: TransferStatus;
  warehouse_id?: string;
}

export function useTransferList(
  filters: TransferFilters,
  limit?: number,
): CursorQueryResult<TransferSummary> {
  return useCursorList(
    inventoryKeys.transfers.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listTransfers(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

export type TransferSnapshot = Awaited<ReturnType<typeof getTransfer>>;

export function useTransferDetail(transferId: string | undefined) {
  return useQuery({
    queryKey: inventoryKeys.transfers.detail(transferId ?? ""),
    queryFn: (): Promise<TransferSnapshot> => getTransfer(transferId as string),
    enabled: transferId !== undefined && transferId !== "",
  });
}

// ── Cycle counts ─────────────────────────────────────────────────────────

export interface CountFilters extends ListFilters {
  status?: CountWorksheetStatus;
  warehouse_id?: string;
  location_id?: string;
  category_id?: string;
}

export function useCountList(
  filters: CountFilters,
  limit?: number,
): CursorQueryResult<CountWorksheetSummary> {
  return useCursorList(
    inventoryKeys.counts.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listCountWorksheets(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

export type CountEntrySnapshot = Awaited<ReturnType<typeof getCountWorksheetEntry>>;
export type CountReviewSnapshot = Awaited<ReturnType<typeof getCountWorksheetReview>>;

/** Blind entry payload — the counter's screen never asks for the review view. */
export function useCountEntry(worksheetId: string | undefined) {
  return useQuery({
    queryKey: inventoryKeys.counts.entry(worksheetId ?? ""),
    queryFn: (): Promise<CountEntrySnapshot> => getCountWorksheetEntry(worksheetId as string),
    enabled: worksheetId !== undefined && worksheetId !== "",
  });
}

/** Review payload — only mounted behind `inventory.count.review`. */
export function useCountReview(worksheetId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.counts.review(worksheetId ?? ""),
    queryFn: (): Promise<CountReviewSnapshot> => getCountWorksheetReview(worksheetId as string),
    enabled: worksheetId !== undefined && worksheetId !== "" && enabled,
  });
}

// ── Analytics ────────────────────────────────────────────────────────────

export interface ReportMeta {
  as_of: string;
  group_by: ReportGroupBy;
  bucket_definitions: readonly BucketDefinition[];
  /** Dead stock only: the `days` threshold the server actually applied. */
  threshold_days?: number;
  /** Slow movers only: the analysis window the server actually applied. */
  analysis_window_days?: number;
}

export interface ReportQueryResult<T> {
  table: CursorQueryResult<T>;
  meta: ReportMeta | null;
}

function useReportCursor<TPage extends { next_cursor: string | null }, TItem>(
  queryKey: readonly unknown[],
  fetchPage: (cursor: string | null, signal: AbortSignal | undefined) => Promise<TPage>,
  selectItems: (page: TPage) => readonly TItem[],
  selectMeta: (page: TPage) => ReportMeta,
  enabled: boolean,
): ReportQueryResult<TItem> {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) => fetchPage(pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: TPage) => lastPage.next_cursor,
    enabled,
  });

  const { data } = query;
  const rows = useMemo(
    () => (data?.pages ?? []).flatMap((page) => [...selectItems(page)]),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectors are pure and stable per call site
    [data],
  );
  const firstPage = data?.pages[0];
  const meta = useMemo(
    () => (firstPage === undefined ? null : selectMeta(firstPage)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectors are pure and stable per call site
    [firstPage],
  );

  return {
    table: {
      rows,
      fetchNextPage: () => void query.fetchNextPage(),
      hasNextPage: query.hasNextPage,
      isLoading: query.isLoading,
      isError: query.isError,
      error: query.error,
      refetch: () => void query.refetch(),
      isFetchingNextPage: query.isFetchingNextPage,
    },
    meta,
  };
}

export interface DeadStockParams {
  /** REQUIRED by the contract; the screen never issues a request without it. */
  days: number;
  group_by?: ReportGroupBy;
  warehouse_id?: string;
  category_id?: string;
  sort?: SortState;
  limit?: number;
}

export function useDeadStockReport(
  params: DeadStockParams,
  enabled = true,
): ReportQueryResult<DeadStockItem> {
  const { days, group_by, warehouse_id, category_id, sort, limit } = params;
  return useReportCursor<DeadStockPage, DeadStockItem>(
    inventoryKeys.reports.deadStock({
      days,
      group_by,
      warehouse_id,
      category_id,
      sort: sort?.key,
      dir: sort?.dir,
      limit,
    }),
    (cursor, signal) => {
      const query: DeadStockQuery = {
        days,
        ...(group_by === undefined ? {} : { group_by }),
        ...(warehouse_id === undefined ? {} : { warehouse_id }),
        ...(category_id === undefined ? {} : { category_id }),
        ...(sort ? { sort: `${sort.key}:${sort.dir}` } : {}),
        ...(limit === undefined ? {} : { limit }),
        ...(cursor === null ? {} : { cursor }),
      };
      return getDeadStockReport(query, signal);
    },
    (page) => page.items,
    (page) => ({
      as_of: page.as_of,
      group_by: page.group_by,
      bucket_definitions: page.bucket_definitions,
      threshold_days: page.threshold_days,
    }),
    enabled,
  );
}

export interface SlowMoverParams {
  /** REQUIRED by the contract; the screen never issues a request without it. */
  days: number;
  group_by?: ReportGroupBy;
  warehouse_id?: string;
  category_id?: string;
  sort?: SortState;
  limit?: number;
}

export function useSlowMoversReport(
  params: SlowMoverParams,
  enabled = true,
): ReportQueryResult<SlowMoverItem> {
  const { days, group_by, warehouse_id, category_id, sort, limit } = params;
  return useReportCursor<SlowMoverPage, SlowMoverItem>(
    inventoryKeys.reports.slowMovers({
      days,
      group_by,
      warehouse_id,
      category_id,
      sort: sort?.key,
      dir: sort?.dir,
      limit,
    }),
    (cursor, signal) => {
      const query: SlowMoverQuery = {
        days,
        ...(group_by === undefined ? {} : { group_by }),
        ...(warehouse_id === undefined ? {} : { warehouse_id }),
        ...(category_id === undefined ? {} : { category_id }),
        ...(sort ? { sort: `${sort.key}:${sort.dir}` } : {}),
        ...(limit === undefined ? {} : { limit }),
        ...(cursor === null ? {} : { cursor }),
      };
      return getSlowMoversReport(query, signal);
    },
    (page) => page.items,
    (page) => ({
      as_of: page.as_of,
      group_by: page.group_by,
      bucket_definitions: page.bucket_definitions,
      analysis_window_days: page.analysis_window_days,
    }),
    enabled,
  );
}

export function useVariantStats(
  variantId: string | undefined,
  query: VariantStatsQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: inventoryKeys.reports.variantStats(variantId ?? "", { ...query }),
    queryFn: ({ signal }) => getVariantStats(variantId as string, query, signal),
    enabled: variantId !== undefined && variantId !== "" && enabled,
  });
}

// ── Lookups ──────────────────────────────────────────────────────────────

export function useVariantSearch(q: string, enabled = true) {
  return useQuery<VariantOption[]>({
    queryKey: inventoryKeys.lookups.variants(q),
    queryFn: ({ signal }) => searchVariants(q, signal),
    enabled,
  });
}

export function useUomOptions(enabled = true) {
  return useQuery<UomOption[]>({
    queryKey: inventoryKeys.lookups.uoms(),
    queryFn: ({ signal }) => listActiveUoms(signal),
    enabled,
  });
}

export function useCategoryOptions(enabled = true) {
  return useQuery<CategoryOption[]>({
    queryKey: inventoryKeys.lookups.categories(),
    queryFn: ({ signal }) => listCategories(signal),
    enabled,
  });
}

export function useBranchOptions(enabled = true) {
  return useQuery<BranchOption[]>({
    queryKey: inventoryKeys.lookups.branches(),
    queryFn: ({ signal }) => listBranches(signal),
    enabled,
  });
}

export function useBatchOptions(
  variantId: string | undefined,
  warehouseId: string | undefined,
  enabled = true,
) {
  const params = {
    ...(variantId === undefined || variantId === "" ? {} : { variant_id: variantId }),
    ...(warehouseId === undefined || warehouseId === "" ? {} : { warehouse_id: warehouseId }),
  };
  return useQuery<Batch[]>({
    queryKey: inventoryKeys.lookups.batches(params),
    queryFn: ({ signal }) => lookupBatches(params, signal),
    enabled: enabled && variantId !== undefined && variantId !== "",
  });
}
